import * as React from "react";
import * as Palette from "../../../../palette";
import { servicePath, userUrl } from "../../../routes";
import { pluralize } from "../../../../core/helpers";
import { advancedSearchPath } from "../../search/routes";
import { SearchResultTag } from "./search-result-tag";
import { PageLayout } from "../../../../palette/layout/page-layout";
import styled from "styled-components";
import FailedIcon from "@atlaskit/icon/glyph/jira/failed-build-status";
import bulkEditServicesQuery from "../models/services.query.graphql";
import {
  bulkEditServices,
  bulkEditServices_search_edges_node_Service,
  bulkEditServices_search_edges
} from "../models/__generated__/bulkEditServices";
import {
  isSufficientPermission,
  Permission,
  ChildProps,
  compose,
  graphql
} from "@atlassiansox/microscopekit";
const AkAddonIcon = require("@atlaskit/icon/glyph/addon").default;
const AkPersonIcon = require("@atlaskit/icon/glyph/person").default;

type Services = bulkEditServices_search_edges_node_Service;
const ErrorSpan = styled.span`
  color: #de350b;
  font-size: 12px;
  margin-left: -2px;
`;
const UnselectButton = styled(Palette.Button)`
  position: relative;
  top: -2px;
`;
const MarginLeftSpan = styled.span`
  margin-left: 5px;
`;

type SelectStepProps = ChildProps<
  {
    query: string;
    setServices: (services: Services[]) => void;
    defaultServices?: Services[];
  },
  bulkEditServices
>;

export type SelectedServices = { [key: string]: true };
export type ServicesWithErrors = { [key: string]: string };

type State = {
  selectedIds: SelectedServices;
  servicesWithErrors: ServicesWithErrors;
};

const limit = 100;
const noPermError = "You don't have permission to update this service.";
export class SelectStepView extends React.Component<SelectStepProps, State> {
  constructor(props: SelectStepProps) {
    super(props);

    let selected: SelectedServices = {};
    if (props.defaultServices) {
      props.defaultServices.forEach(s => {
        selected[s.id] = true;
      });
    }

    this.state = {
      selectedIds: selected,
      servicesWithErrors: {}
    };
  }

  render() {
    const { query } = this.props;
    const services = this.getCurrentServices();

    if (this.props.data!.networkStatus === 1) {
      return this.renderSpinner();
    }

    const errorsCount = Object.keys(this.state.servicesWithErrors).length;
    const selectedCount = Object.keys(this.state.selectedIds).length;

    return (
      <PageLayout title="Choose Services">
        {errorsCount > 0 ? this.renderErrors(errorsCount) : null}

        <p>
          Found {services.length}{" "}
          {pluralize(services.length, "service", "services")} for search query{" "}
          <Palette.Code>{query}</Palette.Code>
          &nbsp;selected {selectedCount}
        </p>

        <Palette.Table
          className="bulk-edit-services-list"
          head={[
            <input
              type="checkbox"
              checked={this.isAllSelected()}
              onChange={this.onSelectAll}
            />,
            "",
            "",
            "",
            "Owner",
            "Platform"
          ]}
          format={[
            ["small", "center"],
            errorsCount > 0 ? ["small", "center"] : "none",
            ["big", "center"],
            ["small", "center"],
            ["small", "center"]
          ]}
          cells={services.map(result => this.renderServiceRow(result))}
        />
      </PageLayout>
    );
  }

  renderErrors(errorsCount: number) {
    return (
      <Palette.Message
        appearance="error"
        title={`Error: ${errorsCount} ${pluralize(
          errorsCount,
          "service",
          "services"
        )} can't be updated`}
      >
        <p>
          Found{" "}
          <strong>
            {errorsCount} {pluralize(errorsCount, "service", "services")}
          </strong>{" "}
          that can't be updated because you don't have permissions.
        </p>
        <br />
        <div>
          Please fix the issues or&nbsp;
          <UnselectButton
            appearance="primary"
            onClick={this.onUnselectServicesWithErrors}
          >
            unselect
          </UnselectButton>
          &nbsp;these services
        </div>
      </Palette.Message>
    );
  }

  renderServiceRow({ id, metadata }: Services) {
    const { tags, platform, owner } = metadata
      ? metadata
      : { tags: null, platform: "", owner: { id: "" } };

    const checkboxEl = (
      <input
        type="checkbox"
        checked={this.state.selectedIds[id] || false}
        onClick={this.onSelect(id)}
      />
    );

    const hasError = id in this.state.servicesWithErrors;
    const error = this.state.servicesWithErrors[id];
    const errorEl = hasError ? (
      <MarginLeftSpan title={error}>
        <Palette.Icon>
          <FailedIcon size="medium" primaryColor="#DE350B" label="Error" />
        </Palette.Icon>
      </MarginLeftSpan>
    ) : (
      ""
    );

    const nameEl = (
      <span>
        <Palette.Link to={servicePath(id)}>
          <Palette.Icon>
            <AkAddonIcon label="Service" />
          </Palette.Icon>
          <span className="search-result-name">{id}</span>
        </Palette.Link>
        {hasError ? (
          <span className="service-error">
            <br />
            <ErrorSpan>{error}</ErrorSpan>
          </span>
        ) : null}
      </span>
    );

    const tagsEl = (
      <SearchResultTag>
        {(tags || []).map(tag => (
          <Palette.Link
            key={tag}
            to={advancedSearchPath("services", `tags:${tag}`, 1)}
          >
            <Palette.Badge>{tag}</Palette.Badge>
          </Palette.Link>
        ))}
      </SearchResultTag>
    );

    const ownerEl = (
      <Palette.Link to={userUrl(owner.id)} target="_blank">
        <Palette.Icon>
          <AkPersonIcon label="User" />
        </Palette.Icon>
        {owner.id}
      </Palette.Link>
    );

    return [checkboxEl, errorEl, nameEl, tagsEl, ownerEl, platform];
  }

  renderSpinner() {
    return (
      <div>
        <PageLayout title="Choose Services" />
        <Palette.Spinner />
      </div>
    );
  }

  isAllSelected() {
    return !!(
      this.props.data!.search &&
      this.props.data!.search!.edges &&
      this.props.data!.search!.edges!.length > 0 &&
      this.props.data!.search!.edges!.length ===
        Object.keys(this.state.selectedIds).length
    );
  }

  setServices = (selectedServices: SelectedServices) => {
    let servicesWithErrors = ([{}, ...this.getCurrentServices()] as any).reduce(
      (map: ServicesWithErrors, service: any) => {
        if (
          selectedServices[service.id] &&
          !isSufficientPermission(
            service.currentUserPermission,
            Permission.Write
          )
        ) {
          map[service.id] = noPermError;
        }
        return map;
      }
    );
    this.setState({ selectedIds: selectedServices, servicesWithErrors });
    let services: Services[] = [];
    if (Object.keys(servicesWithErrors).length === 0) {
      services = this.getCurrentServices().filter(
        s => s.id in selectedServices
      );
    }
    this.props.setServices(services);
  };

  onSelectAll = (_e: any) => {
    const selectedIds: SelectedServices = {};
    const services = this.getCurrentServices();
    if (!this.isAllSelected()) {
      for (const { id } of services) {
        selectedIds[id] = true;
      }
    }
    this.setServices(selectedIds);
  };

  onSelect(id: string) {
    return (_e: any) => {
      const selectedIds = { ...this.state.selectedIds };
      if (id in selectedIds) {
        delete selectedIds[id];
      } else {
        selectedIds[id] = true;
      }
      this.setServices(selectedIds);
    };
  }

  onUnselectServicesWithErrors = (e: any) => {
    e.preventDefault();
    const selectedIds: SelectedServices = {};
    for (const id in this.state.selectedIds) {
      if (!(id in this.state.servicesWithErrors)) {
        selectedIds[id] = true;
      }
    }
    this.setServices(selectedIds);
  };

  getCurrentServices(): Services[] {
    const edges = (this.props.data!.search && this.props.data!.search!.edges
      ? this.props.data!.search!.edges!
      : []
    ).filter((e: any) => e.node);

    return edges.map(
      (e: bulkEditServices_search_edges | null) => e!.node
    ) as Services[];
  }
}

export const SelectStep = compose(
  graphql(bulkEditServicesQuery, {
    options: (props: SelectStepProps) => ({
      errorPolicy: "all",
      variables: {
        query: props.query,
        first: limit + 1
      }
    })
  })
)(SelectStepView);
SelectStep.displayName = "SelectStep";
